import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import useTheme from 'theme/useTheme';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  Container, Image, Icon, Label, Modal, Button, Popup,
} from 'semantic-ui-react';
import moment from 'moment';
import actions from 'actions';
import util from 'utils/utils';
import api from 'api';

import { Sticky, RoundButton } from 'components/lib/UI';
import { UserChip, IdeaFollowChip } from 'components/lib/Chips';
import { IdeaChallengeHelp, SubmitIdeaButton } from 'components/lib/Ideas';

const MobileContainer = styled.div`
  width: 100%;
  padding: 0 5px;
`;

const IdeaStatusBarDiv = styled.div`
  height: ${({ theme }) => (theme.sizes.isMobile ? 40 : 50)}px;
  padding: ${({ theme }) => (theme.sizes.isMobile ? '5px 5px' : '7.5px 15px')};
  background-image: ${({ theme }) => `linear-gradient(to bottom right, ${theme.secondaryColour}, ${util.adjustColour(theme.secondaryColour, 0.5)})`};
  display: flex;
  align-items: center;
  top: 50px;
  width:100%;
`;

const StatusLabel = styled(Label)`
  min-width: max-content;
  height: 25px;
  display: flex !important;
  align-items: center;
`;

const StatusbarInnerContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  align-items: center;
  flex-grow: ${({ flexGrow }) => flexGrow};
  min-width: ${({ flexGrow }) => (flexGrow === 0 ? 'max-content' : 0)};
`;

const StatusbarContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  ${StatusbarInnerContainer} {
    &:not(:first-of-type) {
      margin-left: 15px;
    }
  }
  .submit-idea-button {
    ${({ theme }) => (theme.sizes.isMobile ? `
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      min-width: 150px !important;
      > i {
        height: 1em !important;
      }
    ` : '')}
  }
`;

const IdeaTitle = styled.div`
  color: rgb(255, 255, 255);
  max-height: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
  > span, a {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledRoundButton = styled(RoundButton)`
  margin-left: 3px;
  margin-right: 0 !important;
  height: 30px;
  min-width: max-content;
`;

const IdeaStatusBar = ({
  idea,
  updateIdea,
  isSaving,
  isEditing,
  setEditing,
  onSubmit,
  user,
  assessment,
  submitAssessment,
  isSubmittingAssessment,
  t,
  otherUsers = {},
  websocketConnected,
  incorrectFields,
  isSubmissible,
  scrollToRequired,
}) => {
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const canShowHelpModal = useMemo(() => idea?.ownerChallenge?.stage === 'published' && isEditing, [idea, isEditing]);
  const otherUsersValues = useMemo(() => Object.values(otherUsers), [otherUsers]);
  const theme = useTheme();

  const StatusbarOuterContainer = useMemo(() => (theme.sizes.isMobile ? MobileContainer : Container), [theme]);

  return (
    <Sticky top={50} style={{ zIndex: 300 }}>
      <IdeaStatusBarDiv theme={theme}>
        {canShowHelpModal
          ? (
            <Modal
              open={helpModalOpen}
              onClose={() => setHelpModalOpen(false)}
              style={{ padding: 20, backgroundColor: '#fff' }}
            >
              <IdeaChallengeHelp challengeId={idea.challenge} closeHelp={() => setHelpModalOpen(false)} />
            </Modal>
          ) : null
        }

        <StatusbarOuterContainer>
          <StatusbarContainer>
            {!theme.sizes.isMobile
              ? (
                <StatusbarInnerContainer flexGrow={0}>
                  <IdeaFollowChip idea={idea} style={{ marginRight: 5, height: 25 }} />

                  {idea.isSubmitted
                    ? <StatusLabel size='tiny' color='teal'><Icon name='check' /> Submitted {theme.sizes.isComputer && moment(idea.submittedAt).fromNow()}</StatusLabel>
                    : <StatusLabel size='tiny'><Icon name='file outline' /> Draft</StatusLabel>
                  }
                </StatusbarInnerContainer>
              ) : null
            }

            <StatusbarInnerContainer flexGrow={1}>
              <IdeaTitle>
                {idea.ownerChallenge
                  ? (
                    <span style={{ fontSize: 11, color: 'white' }}>
                      <Link style={{ color: '#fff', textDecorationLine: 'underline', textOverflow: 'ellipsis' }} to={`/challenges/${idea.ownerChallenge._id}?noRedirect`}>
                        {theme.sizes.isMobile ? 'Return to challenge' : `in ${idea.ownerChallenge?.name}`}
                      </Link>
                    </span>
                  ) : null
                }
              </IdeaTitle>
              {canShowHelpModal && !theme.sizes.isMobile
                ? (
                  <Popup
                    header='View challenge details'
                    trigger={(
                      <Button
                        size='mini'
                        icon='eye'
                        onClick={() => setHelpModalOpen(true)}
                        style={{
                          marginLeft: 5, height: 25, display: 'flex', alignItems: 'center',
                        }}
                        inverted
                        basic
                      />
                    )}
                  />
                ) : null
              }
            </StatusbarInnerContainer>

            <StatusbarInnerContainer flexGrow={0}>
              {(isEditing && !websocketConnected && !theme.sizes.isMobile)
                ? <Popup
                  header='Connection issues'
                  content='Collaboration features are disabled. Your ideas will still be saved automatically.'
                  trigger={<Icon color='yellow' size='large' name='warning sign' />}
                />
                : null}

              {!theme.sizes.isMobile
                ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {otherUsersValues.length ? (
                      <Popup
                        header='Collaborators online'
                        content={otherUsersValues.map((u) => u.profile?.fullName).join(', ')}
                        trigger={
                          <div>
                            {otherUsersValues.map((u, index) => (
                              <Image key={u._id} avatar src={util.avatarUrl(u)} style={{ marginLeft: index === 0 ? 0 : -8 }} />
                            ))}
                          </div>
                        }
                      />
                    ) : null}
                  </div>
                ) : null
              }
              <div style={{ display: 'flex' }}>
                {(idea?.isAssessor && !idea?.ideaTemplate?.assessmentClosed && !assessment?.isSubmitted)
                  ? <>
                    {assessment?.conflictOfInterest?.hasConflictOfInterest
                      ? <Popup trigger={<RoundButton style={{ marginLeft: 3 }} size='tiny' content='Submit assessment' primary onClick={submitAssessment} />} content='Conflict of interest declared. When you submit this assessment, all assessment scores/notes will be cleared.' loading={isSubmittingAssessment} />
                      : <RoundButton style={{ marginLeft: 3 }} size='tiny' content='Submit assessment' primary onClick={submitAssessment} loading={isSubmittingAssessment} />}
                  </> : null
                }

                {(util.canEditIdea(user, idea) || util.canManageChallenge(user, idea.ownerChallenge)) && !idea.children
                  ? (
                    <>
                      {!idea.isSubmitted && util.canEditIdea(user, idea) && !theme.sizes.isMobile
                        ? (
                          <StyledRoundButton
                            size='tiny'
                            content={t(isEditing ? 'ideas.header.preview' : 'ideas.header.edit')}
                            onClick={() => {
                              setEditing(!isEditing);
                              if (isEditing) {
                                api.journey.record('ideas', idea._id, 'previewedIdea');
                              } else {
                                api.journey.record('ideas', idea._id, 'resumedEditingIdea');
                              }
                            }}
                            icon='edit'
                            secondary
                          />
                        ) : null
                      }

                      <SubmitIdeaButton
                        user={user}
                        idea={idea}
                        updateIdea={updateIdea}
                        onSubmit={onSubmit}
                        incorrectFields={incorrectFields}
                        isSubmissible={isSubmissible}
                        scrollToRequired={scrollToRequired}
                        isSaving={isSaving}
                      />
                    </>
                  ) : <UserChip inverted user={idea.owner} />
                }
              </div>
            </StatusbarInnerContainer>
          </StatusbarContainer>
        </StatusbarOuterContainer>
      </IdeaStatusBarDiv>
    </Sticky>
  );
};

const mapDispatchToProps = (dispatch) => ({
  update: (id, challenge) => dispatch(actions.challenges.update(id, challenge)),
});
const IdeaStatusBarContainer = withRouter(connect(
  null, mapDispatchToProps,
)(IdeaStatusBar));

export default withTranslation()(IdeaStatusBarContainer);
